import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/AppIcon";
import { cn } from "../../utils/cn";
import ModuleBrochure, {
  type BrochureFeature,
  type BrochureMetric,
  type BrochureUseCase,
} from "./ModuleBrochure";

/**
 * Projects brochure — Phase 6 build for the "work, people, budget connected"
 * pitch. Lives at the end of the catalog because Projects is a discovery
 * surface; the people who click in already have HR in place and are looking
 * for a way to avoid running Asana + spreadsheets next to Mowazaf.
 *
 * Illustration: a synthetic three-column kanban (Backlog / In Progress /
 * Done) with mini task cards in each column, plus a floating "On track"
 * accent pill. All Tailwind + a small atmosphere SVG; no stock.
 *
 * Mockup: a faux task board — three columns, two cards each, every card
 * showing a title, an assignee avatar, and a tiny due-date chip. Right
 * sidebar carries a "Sprint" stat card so the page tells a numerical
 * story alongside the visual one.
 */

// ----- Illustration: 3-column kanban with task cards + "On track" pill -----

interface MiniCard {
  titleKey: string;
  initials: string;
  avatarTint: string;
  dueKey?: string;
}

const MiniTaskCard: React.FC<MiniCard> = ({ titleKey, initials, avatarTint, dueKey }) => {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-[0_4px_12px_-6px_rgba(15,23,42,0.20)]">
      <p className="text-[9px] font-semibold leading-tight text-slate-800">{t(titleKey)}</p>
      <div className="mt-1.5 flex items-center justify-between">
        <span
          className={cn(
            "inline-flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-bold",
            avatarTint,
          )}
        >
          {initials}
        </span>
        {dueKey && (
          <span className="rounded-md bg-slate-100 px-1 py-0.5 text-[8px] font-semibold tabular-nums text-slate-600">
            {t(dueKey)}
          </span>
        )}
      </div>
    </div>
  );
};

const ProjectsIllustration: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";

  return (
    <div className="relative aspect-[4/5] w-full">
      {/* Background atmosphere — soft teal radial wash. */}
      <svg
        viewBox="0 0 320 400"
        className="absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="proj-glow" cx="50%" cy="50%" r="55%">
            <stop offset="40%" stopColor="rgb(20 184 166 / 0.0)" />
            <stop offset="100%" stopColor="rgb(20 184 166 / 0.12)" />
          </radialGradient>
        </defs>
        <rect width="320" height="400" fill="url(#proj-glow)" />
      </svg>

      {/* Kanban frame — rendered as a single white panel with three columns. */}
      <div className="absolute inset-x-3 top-[10%] bottom-[10%] rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.30)]">
        <div className="grid h-full grid-cols-3 gap-2">
          {/* Backlog */}
          <div className="flex min-h-0 flex-col gap-1.5 rounded-lg bg-slate-50 p-1.5">
            <div className="flex items-center justify-between px-1">
              <p className="text-[8px] font-semibold uppercase tracking-wider text-slate-500">
                {t("brochure.projects.illustration.column.backlog")}
              </p>
              <span className="text-[8px] font-semibold tabular-nums text-slate-400">3</span>
            </div>
            <MiniTaskCard
              titleKey="brochure.projects.illustration.task.brief"
              initials="RA"
              avatarTint="bg-indigo-100 text-indigo-700"
            />
            <MiniTaskCard
              titleKey="brochure.projects.illustration.task.research"
              initials="SM"
              avatarTint="bg-amber-100 text-amber-800"
            />
          </div>

          {/* In Progress */}
          <div className="flex min-h-0 flex-col gap-1.5 rounded-lg bg-teal-50 p-1.5">
            <div className="flex items-center justify-between px-1">
              <p className="text-[8px] font-semibold uppercase tracking-wider text-teal-700">
                {t("brochure.projects.illustration.column.inProgress")}
              </p>
              <span className="text-[8px] font-semibold tabular-nums text-teal-700">2</span>
            </div>
            <MiniTaskCard
              titleKey="brochure.projects.illustration.task.design"
              initials="HK"
              avatarTint="bg-emerald-100 text-emerald-700"
              dueKey="brochure.projects.illustration.due.tomorrow"
            />
            <MiniTaskCard
              titleKey="brochure.projects.illustration.task.review"
              initials="OF"
              avatarTint="bg-sky-100 text-sky-700"
              dueKey="brochure.projects.illustration.due.threeDays"
            />
          </div>

          {/* Done */}
          <div className="flex min-h-0 flex-col gap-1.5 rounded-lg bg-slate-50 p-1.5">
            <div className="flex items-center justify-between px-1">
              <p className="text-[8px] font-semibold uppercase tracking-wider text-emerald-700">
                {t("brochure.projects.illustration.column.done")}
              </p>
              <span className="text-[8px] font-semibold tabular-nums text-emerald-700">5</span>
            </div>
            <MiniTaskCard
              titleKey="brochure.projects.illustration.task.kickoff"
              initials="LT"
              avatarTint="bg-violet-100 text-violet-700"
            />
            <MiniTaskCard
              titleKey="brochure.projects.illustration.task.scope"
              initials="RA"
              avatarTint="bg-indigo-100 text-indigo-700"
            />
          </div>
        </div>
      </div>

      {/* "On track" floating pill — top-trailing corner, accent-colored. */}
      <div
        className={cn(
          "absolute top-[4%] flex items-center gap-1.5 rounded-full bg-teal-600 px-3 py-1.5",
          "text-[10px] font-semibold text-white shadow-[0_10px_24px_-8px_rgba(13,148,136,0.55)]",
          isRtl ? "left-[6%]" : "right-[6%]",
        )}
      >
        <Icon name="CircleCheck" size={12} aria-hidden="true" />
        <span>{t("brochure.projects.illustration.onTrack")}</span>
      </div>
    </div>
  );
};

// ----- Mockup: faux task board — 3 columns × 2 cards + sprint sidebar -----

interface BoardCard {
  titleKey: string;
  initials: string;
  avatarTint: string;
  dueKey: string;
}

interface BoardColumn {
  headingKey: string;
  count: number;
  headingTint: string;
  cards: BoardCard[];
}

const COLUMNS: BoardColumn[] = [
  {
    headingKey: "brochure.projects.mockup.column.backlog",
    count: 3,
    headingTint: "text-slate-500",
    cards: [
      {
        titleKey: "brochure.projects.mockup.task.copy",
        initials: "RA",
        avatarTint: "bg-indigo-100 text-indigo-700",
        dueKey: "brochure.projects.mockup.due.5d",
      },
      {
        titleKey: "brochure.projects.mockup.task.qa",
        initials: "SM",
        avatarTint: "bg-amber-100 text-amber-800",
        dueKey: "brochure.projects.mockup.due.7d",
      },
    ],
  },
  {
    headingKey: "brochure.projects.mockup.column.inProgress",
    count: 5,
    headingTint: "text-teal-700",
    cards: [
      {
        titleKey: "brochure.projects.mockup.task.dashboard",
        initials: "HK",
        avatarTint: "bg-emerald-100 text-emerald-700",
        dueKey: "brochure.projects.mockup.due.3d",
      },
      {
        titleKey: "brochure.projects.mockup.task.api",
        initials: "OF",
        avatarTint: "bg-sky-100 text-sky-700",
        dueKey: "brochure.projects.mockup.due.4d",
      },
    ],
  },
  {
    headingKey: "brochure.projects.mockup.column.done",
    count: 7,
    headingTint: "text-emerald-700",
    cards: [
      {
        titleKey: "brochure.projects.mockup.task.spec",
        initials: "LT",
        avatarTint: "bg-violet-100 text-violet-700",
        dueKey: "brochure.projects.mockup.due.done",
      },
      {
        titleKey: "brochure.projects.mockup.task.kickoff",
        initials: "RA",
        avatarTint: "bg-indigo-100 text-indigo-700",
        dueKey: "brochure.projects.mockup.due.done",
      },
    ],
  },
];

const ProjectsMockup: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
      {/* Task board */}
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Icon name="LayoutGrid" size={14} className="text-slate-500" aria-hidden="true" />
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t("brochure.projects.mockup.boardHeading")}
            </p>
          </div>
          <p className="text-[10px] font-medium text-slate-400">
            {t("brochure.projects.mockup.boardProject")}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3 bg-white p-3">
          {COLUMNS.map((col, ci) => (
            <div key={ci} className="flex min-w-0 flex-col gap-2 rounded-md bg-slate-50 p-2">
              <div className="flex items-center justify-between px-1">
                <p
                  className={cn(
                    "text-[10px] font-semibold uppercase tracking-wider",
                    col.headingTint,
                  )}
                >
                  {t(col.headingKey)}
                </p>
                <span className="text-[10px] font-semibold tabular-nums text-slate-400">
                  {col.count}
                </span>
              </div>
              {col.cards.map((c, i) => (
                <div
                  key={i}
                  className="rounded-md border border-slate-200 bg-white p-2 shadow-[0_2px_6px_-2px_rgba(15,23,42,0.10)]"
                >
                  <p className="text-[10px] font-semibold leading-tight text-slate-800">
                    {t(c.titleKey)}
                  </p>
                  <div className="mt-2 flex items-center justify-between">
                    <span
                      className={cn(
                        "inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold",
                        c.avatarTint,
                      )}
                    >
                      {c.initials}
                    </span>
                    <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold tabular-nums text-slate-600">
                      {t(c.dueKey)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Sprint sidebar */}
      <div className="grid gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {t("brochure.projects.mockup.sprint.heading")}
          </p>
          <div className="mt-2 space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-slate-600">
                <Icon name="ListTodo" size={12} className="text-slate-400" aria-hidden="true" />
                {t("brochure.projects.mockup.sprint.tasks")}
              </span>
              <span className="font-semibold tabular-nums text-slate-900">12</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-slate-600">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {t("brochure.projects.mockup.sprint.done")}
              </span>
              <span className="font-semibold tabular-nums text-emerald-700">7</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-slate-600">
                <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
                {t("brochure.projects.mockup.sprint.inProgress")}
              </span>
              <span className="font-semibold tabular-nums text-teal-700">5</span>
            </div>
          </div>
          {/* Progress bar — 7/12 ≈ 58% */}
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-teal-500"
              style={{ width: "58%" }}
              aria-hidden="true"
            />
          </div>
        </div>
        <div className="rounded-lg border border-teal-200 bg-teal-50 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-teal-800">
            {t("brochure.projects.mockup.burn.heading")}
          </p>
          <p className="mt-1.5 text-2xl font-semibold tabular-nums text-teal-900">
            {t("brochure.projects.mockup.burn.amount")}
          </p>
          <p className="mt-0.5 text-[10px] leading-snug text-teal-800/80">
            {t("brochure.projects.mockup.burn.hint")}
          </p>
        </div>
      </div>
    </div>
  );
};

// ----- Brochure props ------

const ProjectsBrochure: React.FC = () => {
  const { t } = useTranslation();

  const features: BrochureFeature[] = [
    {
      icon: "FolderPlus",
      title: t("brochure.projects.features.setup.title"),
      description: t("brochure.projects.features.setup.description"),
    },
    {
      icon: "LayoutGrid",
      title: t("brochure.projects.features.board.title"),
      description: t("brochure.projects.features.board.description"),
    },
    {
      icon: "Timer",
      title: t("brochure.projects.features.timeTracking.title"),
      description: t("brochure.projects.features.timeTracking.description"),
    },
    {
      icon: "Gauge",
      title: t("brochure.projects.features.capacity.title"),
      description: t("brochure.projects.features.capacity.description"),
    },
    {
      icon: "BarChart3",
      title: t("brochure.projects.features.reports.title"),
      description: t("brochure.projects.features.reports.description"),
    },
  ];

  const metrics: BrochureMetric[] = [
    {
      value: t("brochure.projects.metrics.sourceValue"),
      label: t("brochure.projects.metrics.sourceLabel"),
    },
    {
      value: "30%",
      label: t("brochure.projects.metrics.reportingLabel"),
    },
    {
      value: "0",
      label: t("brochure.projects.metrics.exportsLabel"),
    },
  ];

  const useCases: BrochureUseCase[] = [
    {
      persona: t("brochure.projects.useCases.pm.persona"),
      scenario: t("brochure.projects.useCases.pm.scenario"),
      outcome: t("brochure.projects.useCases.pm.outcome"),
    },
    {
      persona: t("brochure.projects.useCases.member.persona"),
      scenario: t("brochure.projects.useCases.member.scenario"),
      outcome: t("brochure.projects.useCases.member.outcome"),
    },
  ];

  return (
    <ModuleBrochure
      slug="projects"
      hero={{
        eyebrow: t("brochure.projects.hero.eyebrow"),
        headline: t("brochure.projects.hero.headline"),
        subheadline: t("brochure.projects.hero.subheadline"),
        illustration: <ProjectsIllustration />,
      }}
      features={features}
      metrics={metrics}
      mockup={{
        title: t("brochure.projects.mockup.caption"),
        content: <ProjectsMockup />,
      }}
      useCases={useCases}
    />
  );
};

export default ProjectsBrochure;
